/**
 * The simulated hosted checkout (MAR-1130): the page billing's payment simulator redirects to when it
 * runs in hosted mode (`BILLING_SIMULATOR_CHECKOUT=hosted`), standing in for a processor's own card
 * page until a real adapter exists (MAR-1033). When one does, the processor's page replaces this one
 * and the plan step does not change: it already follows a `redirect` and reads the checkout on return.
 *
 * NO CARD DATA LEAVES THE BROWSER (MAR-1039 D8). The page decides the outcome from a test card on the
 * client, the way processors' test cards work, and sends billing only that outcome. Nothing here, and
 * nothing the page sends, carries a card number, CVV or expiry.
 */

/** Where the page lives. `services/billing/src/payments/simulator.ts` redirects here; a test pins the two together. */
export const SIMULATED_CHECKOUT_PATH = '/pago-simulado'

/**
 * Where the simulated HOSTED FIELDS live (MAR-1317): the frame the plan step mounts when billing's
 * simulator runs in embedded mode (`BILLING_SIMULATOR_CHECKOUT=embedded`). Pinned to billing's own
 * constant by a test, as the page's is.
 */
export const SIMULATED_FIELDS_PATH = '/pago-simulado-campos'

/** The simulated provider's pages, which the auth middleware lets a founder who owes the plan step reach. */
export function isSimulatedCheckoutPath(path: string): boolean {
  const bare = path.replace(/\/+$/, '')
  return bare === SIMULATED_CHECKOUT_PATH || bare === SIMULATED_FIELDS_PATH
}

/** What the customer did on the page, and all the page sends. */
export const SIMULATED_OUTCOMES = ['approve', 'decline', 'cancel'] as const
export type SimulatedOutcome = (typeof SIMULATED_OUTCOMES)[number]

/** The test cards the page names, as processors document theirs. Any other card is declined. */
export const SIMULATED_TEST_CARDS = {
  approve: '4242 4242 4242 4242',
  decline: '4000 0000 0000 0002',
} as const

const digitsOf = (value: string) => value.replace(/\D/g, '')

/** The outcome a card chooses: the approving test card approves, everything else is declined. */
export function simulatedCardOutcome(card: string): Exclude<SimulatedOutcome, 'cancel'> {
  return digitsOf(card) === digitsOf(SIMULATED_TEST_CARDS.approve) ? 'approve' : 'decline'
}

/** The simulated fields' 3-D Secure test card (MAR-1317): the issuer asks for a challenge before the outcome. */
export const SIMULATED_AUTHENTICATION_CARD = '4000 0000 0000 3220'

/** What a card does in the simulated fields: approve, decline, or ask for the 3-D Secure challenge first. */
export function simulatedFieldsOutcome(card: string): Exclude<SimulatedOutcome, 'cancel'> | 'authenticate' {
  return digitsOf(card) === digitsOf(SIMULATED_AUTHENTICATION_CARD) ? 'authenticate' : simulatedCardOutcome(card)
}

/**
 * Where the page sends the customer back: the return URL billing gave, if it is on this app's own
 * origin, else `/app/setup` here (never an open redirect), with the checkout's reference added, as a
 * processor adds its session id.
 */
export function checkoutReturnUrl(raw: string | undefined, ref: string, origin: string): string {
  let target = new URL('/app/setup', origin)
  try {
    const given = new URL(raw ?? '', origin)
    if (raw && /^https?:\/\//.test(raw) && given.origin === origin) target = given
  } catch {
    // not a URL: the fallback stands
  }
  target.searchParams.set('checkout', ref)
  return target.toString()
}

/** A checkout as billing reports it: the page's order summary, and what the plan step shows on return. */
export interface CheckoutState {
  ref: string
  /** `open` until the customer pays, is declined or cancels. */
  status: 'open' | 'paid' | 'declined' | 'canceled'
  /** Whether the simulator can pay this checkout now; the page refuses otherwise. */
  simulated: boolean
  description: string
  seats: number
  /** Each line's plan is `plan_id`, its column's name (MAR-1273); billing's own wire says `planId`. */
  lines: { plan_id: string, quantity: number }[]
  /** Billing's amounts, integer cents, IVA included in `totalCents`. */
  amount: { netCents: number, ivaCents: number, totalCents: number }
  currency: string
}

/** What paying (or cancelling) on the page answers. */
export interface SimulatedPaymentResult {
  ref: string
  status: CheckoutState['status']
  seats: number
}

